import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { ArrowDownOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ReactComponent as CloseIcon } from 'images/chatbot/close-icon.svg';
import { ReactComponent as MinimizeIcon } from 'images/chatbot/minimize-close-icon.svg';
import { ReactComponent as NewChatIcon } from 'images/chatbot/new-chat-icon.svg';
import { ReactComponent as DraggableIcon } from 'images/chatbot/draggable.svg';
import { ReactComponent as AiAssistantIcon } from 'images/icon-ai-indicator.svg';
import { ReactComponent as ExpandIcon } from 'images/icons/square.svg';
import {
  Control as Controls,
  ControlsProps,
} from 'modules/chatbot/types/types';

import BrokerChatInput from 'modules/chatbot/components/BrokerChatInput/BrokerChatInput';
import StatusSteps from 'modules/chatbot/components/StatusSteps';
import TermsOfServiceModal from 'modules/chatbot/components/TermsOfServiceModal/TermsOfServiceModal';
import { useChatSSE } from 'modules/chatbot/hooks/useChatSSE';
import { useInitialization } from 'modules/chatbot/hooks/useInitialization';
import {
  selectContext,
  selectConversationId,
  selectHasContext,
  selectIsFullScreen,
  selectIsInputEnabled,
  selectIsTypingAnimate,
  selectIsVisible,
  selectShowNewChatOverlay,
  selectTermsAccepted,
  setFullScreen,
  setShowNewChatOverlay,
  setTermsAccepted,
} from 'modules/chatbot/slices/chatbotSlice';
import { CHATBOT_TEST_IDS } from 'modules/chatbot/constants/testIds';
import useBenguide from 'hooks/benguide';
import styles from './supportChatLayout.module.less';

const createControls = (
  showNewChatOverlay: () => void,
  handleModalClose: () => void,
  toggleFullScreen?: () => void,
  isFullScreen?: boolean
): Controls[] => {
  const controls: Controls[] = [
    {
      icon: <NewChatIcon />,
      action: showNewChatOverlay,
      tooltip: 'Start new chat',
    },
  ];

  // Add fullscreen toggle button if toggle function is provided
  if (toggleFullScreen) {
    controls.push({
      icon: isFullScreen ? <MinimizeIcon /> : <ExpandIcon />,
      action: toggleFullScreen,
      tooltip: isFullScreen ? 'Exit full-screen view' : 'View full-screen',
    });
  }

  // Add close button only if not in fullscreen
  if (!isFullScreen) {
    controls.push({
      icon: <CloseIcon />,
      action: handleModalClose,
    });
  }

  return controls;
};

interface SupportChatLayoutProps {
  children: React.ReactNode;
  handleModalClose: () => void;
  visible: boolean;
  'aria-label'?: string;
  role?: string;
  'aria-modal'?: boolean;
  isFullScreen?: boolean;
}

const SupportChatLayout = ({
  children,
  handleModalClose,
  visible,
  'aria-label': ariaLabel,
  role,
  'aria-modal': ariaModal,
}: SupportChatLayoutProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const benefitGuide = useBenguide();
  const dispatch = useAppDispatch();
  const isVisible = useAppSelector(selectIsVisible);
  const hasContext = useAppSelector(selectHasContext);
  const typingAnimation = useAppSelector(selectIsTypingAnimate);
  const isFullScreen = useAppSelector(selectIsFullScreen);

  // Terms of Service state - use Redux state with localStorage fallback
  const termsAccepted = useAppSelector(selectTermsAccepted);

  // Scroll position tracking state
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const isProgrammaticScrollRef = useRef(false);

  // Check if user is at the very bottom of the chat
  const isAtBottom = useCallback((container: HTMLElement): boolean => {
    const threshold = 200; // Increased threshold to hide arrow sooner
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight <= threshold;
  }, []);

  // Check if there's content that overflows the viewport (actual scrollable content)
  const hasScrollableContent = useCallback(
    (container: HTMLElement): boolean => {
      const { scrollHeight, clientHeight } = container;
      return scrollHeight > clientHeight;
    },
    []
  );

  // Handle scroll events to detect user interaction
  const handleScroll = useCallback(() => {
    const container = chatBodyRef.current;
    if (!container) return;

    // If this is a programmatic scroll, ignore it
    if (isProgrammaticScrollRef.current) {
      return;
    }

    // Only show arrow if there's scrollable content AND user is not at bottom
    const atBottom = isAtBottom(container);
    const hasContent = hasScrollableContent(container);

    setShowJumpToBottom(!atBottom && hasContent);
  }, [isAtBottom, hasScrollableContent]);

  // Scroll to absolute bottom function
  const scrollToBottom = useCallback(
    (smooth = true) => {
      const container = chatBodyRef.current;
      if (!container) return;

      isProgrammaticScrollRef.current = true;

      if (smooth) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      } else {
        container.scrollTop = container.scrollHeight;
      }

      // Reset the flag after scroll animation completes
      setTimeout(
        () => {
          isProgrammaticScrollRef.current = false;
          // Add extra delay to ensure DOM has updated before checking scroll state
          setTimeout(() => {
            const container = chatBodyRef.current;
            if (container) {
              const atBottom = isAtBottom(container);
              const hasContent = hasScrollableContent(container);
              setShowJumpToBottom(!atBottom && hasContent);
            }
          }, 100); // Extra delay for DOM updates
        },
        smooth ? 600 : 50
      );
    },
    [isAtBottom, hasScrollableContent]
  );

  // Scroll by one viewport height
  const scrollByViewport = useCallback(() => {
    const container = chatBodyRef.current;
    if (!container) return;

    isProgrammaticScrollRef.current = true;

    const viewportHeight = container.clientHeight;
    const currentScroll = container.scrollTop;
    const maxScroll = container.scrollHeight - container.clientHeight;

    // Scroll down by viewport height, but not beyond the bottom
    const targetScroll = Math.min(currentScroll + viewportHeight, maxScroll);

    container.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      // Add extra delay to ensure DOM has updated before checking scroll state
      setTimeout(() => {
        const container = chatBodyRef.current;
        if (container) {
          const atBottom = isAtBottom(container);
          const hasContent = hasScrollableContent(container);
          setShowJumpToBottom(!atBottom && hasContent);
        }
      }, 100); // Extra delay for DOM updates
    }, 600);
  }, [isAtBottom, hasScrollableContent]);

  const { initialize } = useInitialization({
    benefitGuideId: benefitGuide?.masterId,
    employerId: benefitGuide?.employerId,
    organizationId: benefitGuide?.orgId,
    autoInitialize: isVisible,
  });

  // Initialize when chatbot becomes visible and no context exists
  useEffect(() => {
    if (
      isVisible &&
      !hasContext &&
      benefitGuide?.masterId &&
      benefitGuide?.employerId &&
      benefitGuide?.orgId
    ) {
      initialize();
    }
  }, [
    isVisible,
    hasContext,
    benefitGuide?.masterId,
    benefitGuide?.employerId,
    benefitGuide?.orgId,
    initialize,
  ]);

  // Use the new SSE-based chat hook - only connect after initialization
  const { messages, sendMessage, isLoading, error, clearError } = useChatSSE({
    autoConnect: true,
    enableRetry: true,
    maxRetries: 2,
    metadata: {
      conversationId: useAppSelector(selectConversationId),
      organizationId: benefitGuide?.orgId,
      employerId: benefitGuide?.employerId,
      context: useAppSelector(selectContext),
    },
  });

  // Check if input should be enabled (last bot message completed)
  const isInputEnabled = useAppSelector(selectIsInputEnabled);
  const showNewChatOverlay = useAppSelector(selectShowNewChatOverlay);

  // Function to toggle the new chat overlay
  const toggleNewChatOverlay = () => {
    dispatch(setShowNewChatOverlay(!showNewChatOverlay));
  };

  // Function to toggle fullscreen mode
  const toggleFullScreen = () => {
    dispatch(setFullScreen(!isFullScreen));
  };

  // Handle jump button click - different behavior based on message state
  const handleJumpToBottom = useCallback(() => {
    const isGenerating = isLoading || typingAnimation?.isTyping;

    if (isGenerating) {
      // While generating: scroll by viewport height
      scrollByViewport();
    } else {
      // After complete: scroll to absolute bottom
      scrollToBottom(true);
      setShowJumpToBottom(false);
    }
  }, [isLoading, typingAnimation?.isTyping, scrollByViewport, scrollToBottom]);

  // Terms of Service handlers
  const handleAcceptTerms = () => {
    // Save to Redux state
    dispatch(setTermsAccepted(true));
  };

  const handleCloseTerms = () => {
    // Close both Terms modal and AI Assistant
    // User must accept terms to use the assistant
    handleModalClose();
  };

  // create control components
  const ChatControls = ({ components }: ControlsProps) => {
    const handleControlClick = (component: any) => {
      if (component?.action && typeof component.action === 'function') {
        component.action();
      } else {
        console.error('Invalid action for control:', component);
      }
    };

    const getControlTestId = (index: number) => {
      const controlNames = isFullScreen
        ? ['new-chat', 'fullscreen']
        : ['new-chat', 'fullscreen', 'close'];
      return `chatbot-control-${controlNames[index] || index}`;
    };

    return (
      <div className={styles.chatControls}>
        {components?.map((component, index) =>
          component.tooltip ? (
            <Tooltip
              key={index}
              title={component.tooltip}
              placement="bottom"
              mouseEnterDelay={0.3}
            >
              <div
                className={styles?.controlIcon}
                onClick={() => handleControlClick(component)}
                data-testid={getControlTestId(index)}
              >
                {component.icon}
              </div>
            </Tooltip>
          ) : (
            <div
              key={index}
              className={styles?.controlIcon}
              onClick={() => handleControlClick(component)}
              data-testid={getControlTestId(index)}
            >
              {component.icon}
            </div>
          )
        )}
      </div>
    );
  };

  const handleSubmit = async (value: string) => {
    if (isLoading || isEmpty(value) || !isInputEnabled) return;

    // Clear any existing errors
    if (error) {
      clearError();
    }

    // Send message via SSE
    await sendMessage(value);
  };

  // Scroll to show new message when a new message is added
  useEffect(() => {
    if (messages.length > 0 && !isLoading) {
      // Use a small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        const container = chatBodyRef.current;
        if (!container) return;

        // Scroll to bottom but leave extra space (about 5-6 lines worth)
        // This ensures we can see the beginning of the response with room for growth
        const targetScroll = container.scrollHeight + container.clientHeight;

        isProgrammaticScrollRef.current = true;

        container.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth',
        });

        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 600);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, messages.length]);

  // Handle initial state when chat becomes visible
  useEffect(() => {
    if (visible) {
      // Reset scroll state and jump button when chat opens
      setShowJumpToBottom(false);

      // Ensure chat starts at the top
      const container = chatBodyRef.current;
      if (container) {
        container.scrollTop = 0;
      }
    }
  }, [visible]);

  // Show jump button during typing animation, but don't auto-scroll
  useEffect(() => {
    const container = chatBodyRef.current;
    if (!container) return;

    const atBottom = isAtBottom(container);
    const hasContent = hasScrollableContent(container);
    const isStreaming = typingAnimation?.isTyping || isLoading;

    // Show arrow only if:
    // 1. There's scrollable content (content exceeds viewport)
    // 2. User is not at bottom
    // 3. Either streaming is active OR conversation history exceeds viewport
    // 4. StatusSteps is NOT showing (when isLoading, StatusSteps shows and arrow should hide)
    const shouldShow =
      hasContent &&
      !atBottom &&
      (isStreaming || messages.length > 1) &&
      !isLoading;

    setShowJumpToBottom(shouldShow);
  }, [
    typingAnimation?.isTyping,
    typingAnimation?.chunkCount,
    isLoading,
    messages.length,
    isAtBottom,
    hasScrollableContent,
  ]);

  // Auto-scroll to bottom when typing finishes and hide arrow
  useEffect(() => {
    // When typing just finished (was typing, now not typing)
    if (!typingAnimation?.isTyping && !isLoading) {
      const container = chatBodyRef.current;
      if (!container) return;

      // Auto-scroll to bottom after typing completes
      setTimeout(() => {
        scrollToBottom(true);
        // Hide arrow after auto-scroll
        setTimeout(() => {
          setShowJumpToBottom(false);
        }, 200); // Wait for scroll animation to complete
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [typingAnimation?.isTyping, isLoading, scrollToBottom]);

  // Attach scroll listener to chat body container
  useEffect(() => {
    const container = chatBodyRef.current;
    if (!container) return;

    // Use passive listener for better performance
    const options = { passive: true, capture: false };

    // Add scroll listener
    container.addEventListener('scroll', handleScroll, options);

    return () => {
      container.removeEventListener('scroll', handleScroll, options);
      // Reset flags on cleanup
      isProgrammaticScrollRef.current = false;
    };
  }, [handleScroll]);

  return (
    <div
      className={`${styles.chatLayoutWrapper} ${
        isFullScreen ? styles.fullScreen : styles.minimized
      }`}
      aria-label={ariaLabel}
      role={role}
      aria-modal={ariaModal}
      data-testid={CHATBOT_TEST_IDS.CONTAINER}
    >
      <div className={styles.chatHeader}>
        <div className={styles.chatHeaderTextWrapper}>
          {!isFullScreen && (
            <DraggableIcon
              className={'drag-handle ' + styles.handleIcon}
              data-testid={CHATBOT_TEST_IDS.DRAG_HANDLE}
            />
          )}
          <AiAssistantIcon />
          <div className={styles.headerText}>Ask SAGE</div>
        </div>
        <div
          className={styles.chatControls}
          data-testid={CHATBOT_TEST_IDS.CONTROLS}
        >
          <ChatControls
            components={createControls(
              toggleNewChatOverlay,
              handleModalClose,
              toggleFullScreen,
              isFullScreen
            )}
          />
        </div>
      </div>
      <div
        ref={chatBodyRef}
        className={styles.chatBody}
        data-testid={CHATBOT_TEST_IDS.CONTAINER}
      >
        <div
          className={styles.messagesContainer}
          data-testid={CHATBOT_TEST_IDS.MESSAGES_CONTAINER}
        >
          {children}
          <StatusSteps
            defaultLoaders={true}
            messageKey={messages.length}
            isLoading={isLoading}
          />
          <div ref={messagesEndRef} />
        </div>
      </div>
      {/* Jump to bottom button - render when there's scrollable content */}
      <div
        className={styles.jumpToBottomContainer}
        data-visible={showJumpToBottom ? 'true' : 'false'}
        aria-hidden={!showJumpToBottom}
      >
        <ArrowDownOutlined
          className={styles.jumpToBottomButton}
          onClick={handleJumpToBottom}
        />
      </div>
      <div
        className={styles.chatFooter}
        data-testid={CHATBOT_TEST_IDS.BOT_MESSAGE_FOOTER}
      >
        <BrokerChatInput
          onSubmit={handleSubmit}
          loading={isLoading || !isInputEnabled}
          autoFocus
          data-testid={CHATBOT_TEST_IDS.INPUT_CONTAINER}
        />
      </div>
      {visible && (
        <TermsOfServiceModal
          visible={!termsAccepted}
          onAccept={handleAcceptTerms}
          onClose={handleCloseTerms}
        />
      )}
    </div>
  );
};

export default SupportChatLayout;
